import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

interface SharedReport {
  id: string;
  client_name: string;
  claimant_name: string;
  business_address: string;
  business_type: string;
  event_type: string;
  event_start_date: string;
  event_end_date: string;
  coverage_year: number;
  result: {
    final_claim_amount: number;
    causation_test_passed: boolean;
    [key: string]: any;
  };
  calculation_mode: string;
  created_at: string;
  financial_summary: {
    years: string[];
    has_reconstructed_data: boolean;
  };
  _share: {
    watermark: string;
    expires_at: string;
    view_count: number;
    read_only: boolean;
  };
}

export default function PublicShareView() {
  const { shareToken } = useParams<{ shareToken: string }>();
  const [report, setReport] = useState<SharedReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadSharedReport = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/v1/collaboration/public/${shareToken}`);
        setReport(response.data);
      } catch (err: any) {
        if (err.response?.status === 404) {
          setError('Share link not found. It may have been revoked.');
        } else if (err.response?.status === 410) {
          setError('This share link has expired.');
        } else {
          setError('Failed to load shared report.');
        }
      } finally {
        setLoading(false);
      }
    };

    if (shareToken) {
      loadSharedReport();
    }
  }, [shareToken]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600">Loading shared report...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">❌</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Link Unavailable</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <Link
            to="/"
            className="inline-block px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
          >
            Go to Homepage
          </Link>
        </div>
      </div>
    );
  }

  if (!report) return null;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Watermark Header */}
      <div className="bg-amber-500 text-white py-2 px-4 text-center text-sm font-medium">
        <span className="mr-2">🔒</span>
        {report._share.watermark} • View #{report._share.view_count} • 
        Expires {new Date(report._share.expires_at).toLocaleDateString()}
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-2xl">📊</span>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{report.client_name}</h1>
                <p className="text-gray-500">Business Interruption Claim Report</p>
              </div>
            </div>
            <span className="px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-sm font-medium">
              READ-ONLY
            </span>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-gray-500">Claimant</p>
              <p className="font-medium">{report.claimant_name || 'N/A'}</p>
            </div>
            <div>
              <p className="text-gray-500">Business Type</p>
              <p className="font-medium">{report.business_type || 'N/A'}</p>
            </div>
            <div>
              <p className="text-gray-500">Event Type</p>
              <p className="font-medium">{report.event_type || 'N/A'}</p>
            </div>
            <div>
              <p className="text-gray-500">Coverage Year</p>
              <p className="font-medium">{report.coverage_year || 'N/A'}</p>
            </div>
          </div>
        </div>

        {/* Claim Amount */}
        {report.result && (
          <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Calculation Result</h2>
            
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-6 text-center">
              <p className="text-sm text-green-700 mb-2">Total Calculated Claim</p>
              <p className="text-4xl font-bold text-green-800">
                {formatCurrency(report.result.final_claim_amount || 0)}
              </p>
              
              <div className="mt-4 flex items-center justify-center gap-2">
                {report.result.causation_test_passed ? (
                  <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm">
                    ✅ Causation Test Passed
                  </span>
                ) : (
                  <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm">
                    ❌ Causation Test Failed
                  </span>
                )}
                <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">
                  {report.calculation_mode === 'future_closure' ? 'Future Closure' : 'Standard BI'}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Event Details */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Event Details</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-500">Event Start Date</p>
              <p className="font-medium">{report.event_start_date || 'Not specified'}</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-500">Event End Date</p>
              <p className="font-medium">{report.event_end_date || 'Not specified'}</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-500">Business Address</p>
              <p className="font-medium">{report.business_address || 'Not specified'}</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-500">Report Created</p>
              <p className="font-medium">{new Date(report.created_at).toLocaleDateString()}</p>
            </div>
          </div>
        </div>

        {/* Financial Data Summary */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Financial Data</h2>
          
          <div className="flex flex-wrap gap-2 mb-4">
            {report.financial_summary?.years?.map((year) => (
              <span key={year} className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">
                {year.replace('data_', '')}
              </span>
            ))}
          </div>
          
          {report.financial_summary?.has_reconstructed_data && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              <strong>📊 Note:</strong> Financial data was reconstructed from bank/credit card statements.
              All estimates are documented in the full report.
            </div>
          )}
        </div>

        {/* PDF Download */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Download Report</h2>
          
          <a
            href={`${BACKEND_URL}/api/v1/collaboration/public/${shareToken}/pdf`}
            className="inline-flex items-center gap-2 px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Download PDF (Read-Only)
          </a>
          
          <p className="text-xs text-gray-500 mt-2">
            PDF includes watermark: "{report._share.watermark}"
          </p>
        </div>

        {/* Footer */}
        <div className="text-center text-sm text-gray-500">
          <p>This is a read-only shared view. To make changes, contact the report owner.</p>
          <p className="mt-2">
            Powered by <span className="font-semibold text-blue-600">DisruptionGenius</span>
          </p>
        </div>
      </div>
    </div>
  );
}
